const MODAL_ID = 'sample-limit-modal';
const COUNTER_SELECTOR = '[data-sample-limit-counter]';
const COUNTER_TEXT_SELECTOR = '.sample-limit-drawer-counter__text';
const CHECKOUT_TRIGGER_SELECTOR =
  'button[name="checkout"], input[name="checkout"], .additional-checkout-buttons, shopify-accelerated-checkout-cart';

/**
 * Free sample limits are a storefront UX guard only. Shopify cart endpoints can still be called
 * directly, so hard enforcement must live in a Shopify Function or app-level validation.
 */

/**
 * @typedef {{ enabled?: boolean, max?: number, currentCount?: number, modalId?: string }} SampleLimitConfig
 */

/** @returns {SampleLimitConfig} */
function getConfig() {
  if (typeof Theme === 'undefined') return {};

  return Theme.sampleLimit ?? {};
}

/** @param {unknown} value */
function toNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

/** @param {string | undefined} value */
export function parseSampleVariantIds(value) {
  if (!value) return [];

  return value
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
}

export function isSampleLimitEnabled() {
  const config = getConfig();
  return config.enabled === true && toNumber(config.max) > 0;
}

export function getSampleLimitMax() {
  return toNumber(getConfig().max);
}

export function getSampleLimitCount() {
  return toNumber(getConfig().currentCount);
}

/**
 * @param {HTMLElement} counter
 * @param {number} count
 */
function updateSampleLimitCounter(counter, count) {
  const max = toNumber(counter.dataset.sampleLimitMax) || getSampleLimitMax();
  const text = counter.querySelector(COUNTER_TEXT_SELECTOR);

  counter.dataset.sampleLimitCountValue = String(count);

  if (!(text instanceof HTMLElement)) return;

  if (count > 0) {
    const strong = document.createElement('strong');
    strong.textContent = `${count} of ${max}`;

    text.replaceChildren(strong, ` ${counter.dataset.sampleLimitCountSuffix || ''}`);
    return;
  }

  text.textContent = (counter.dataset.sampleLimitEmptyText || '').replace('[limit]', String(max));
}

/** @param {number} count */
function syncSampleLimitCounters(count) {
  document.querySelectorAll(COUNTER_SELECTOR).forEach((counter) => {
    if (counter instanceof HTMLElement) {
      updateSampleLimitCounter(counter, count);
    }
  });
}

/** @param {number} count */
export function setSampleLimitCount(count) {
  if (typeof Theme === 'undefined' || Theme.sampleLimit == null) return;

  const normalizedCount = Math.max(0, toNumber(count));

  Theme.sampleLimit.currentCount = normalizedCount;
  syncSampleLimitCounters(normalizedCount);
}

export function syncSampleLimitFromDocument() {
  const countSource = document.querySelector('[data-sample-limit-current-count]');
  if (!(countSource instanceof HTMLElement)) return false;

  setSampleLimitCount(toNumber(countSource.dataset.sampleLimitCurrentCount));
  return true;
}

/**
 * @param {Record<string, string> | undefined} sections
 * @returns {boolean}
 */
export function syncSampleLimitFromSections(sections) {
  if (!sections) return false;

  for (const sectionHTML of Object.values(sections)) {
    const html = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const countSource = html.querySelector('[data-sample-limit-current-count]');

    if (countSource instanceof HTMLElement) {
      setSampleLimitCount(toNumber(countSource.dataset.sampleLimitCurrentCount));
      return true;
    }
  }

  return false;
}

/**
 * @param {string | number | FormDataEntryValue | null | undefined} variantId
 * @param {Element | null | undefined} sourceElement
 */
export function isFreeSampleVariant(variantId, sourceElement) {
  if (variantId == null) return false;

  const normalizedVariantId = String(variantId);
  let element = sourceElement;

  while (element instanceof HTMLElement) {
    if (parseSampleVariantIds(element.dataset.sampleLimitVariantIds).includes(normalizedVariantId)) {
      return true;
    }

    element = element.parentElement;
  }

  return false;
}

export function openSampleLimitModal() {
  const config = getConfig();
  const modalId = config.modalId || MODAL_ID;
  const modal = document.getElementById(modalId);

  if ('showDialog' in (modal ?? {}) && typeof modal.showDialog === 'function') {
    modal.showDialog();
    return;
  }

  const dialog = modal instanceof HTMLDialogElement ? modal : modal?.querySelector('dialog');
  if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;

  if (typeof dialog.showModal === 'function') {
    dialog.showModal();
  } else {
    dialog.setAttribute('open', '');
  }
}

/** @param {number} quantityToAdd */
export function wouldExceedSampleLimit(quantityToAdd) {
  if (!isSampleLimitEnabled()) return false;

  return getSampleLimitCount() + Math.max(0, quantityToAdd) > getSampleLimitMax();
}

/**
 * @param {object} config
 * @param {string | number | FormDataEntryValue | null | undefined} config.variantId
 * @param {number} config.quantity
 * @param {Element | null | undefined} config.sourceElement
 */
export function validateSampleAdd({ variantId, quantity, sourceElement }) {
  if (!isSampleLimitEnabled()) return true;
  if (!isFreeSampleVariant(variantId, sourceElement)) return true;
  if (!wouldExceedSampleLimit(quantity)) return true;

  openSampleLimitModal();
  return false;
}

/**
 * @param {object} config
 * @param {boolean} config.isSample
 * @param {number} config.currentQuantity
 * @param {number} config.nextQuantity
 */
export function validateSampleQuantityChange({ isSample, currentQuantity, nextQuantity }) {
  if (!isSampleLimitEnabled() || !isSample) return true;
  if (nextQuantity <= currentQuantity) return true;

  const delta = nextQuantity - currentQuantity;
  if (!wouldExceedSampleLimit(delta)) return true;

  openSampleLimitModal();
  return false;
}

/**
 * Calculates the free-sample total represented by the cart form's live input
 * values. This catches values typed immediately before checkout, before the
 * debounced Ajax cart update has completed.
 * @param {HTMLFormElement | null} form
 */
function getProjectedSampleCount(form) {
  if (!(form instanceof HTMLFormElement)) return getSampleLimitCount();

  let count = 0;

  for (const row of form.querySelectorAll('[data-sample-limit-sample="true"]')) {
    if (!(row instanceof HTMLElement)) continue;

    const input = row.querySelector('input[name="updates[]"]');
    const inputQuantity = input instanceof HTMLInputElement ? Number.parseInt(input.value, 10) : NaN;
    const currentQuantity = toNumber(row.dataset.sampleLimitQuantity);

    count += Math.max(0, Number.isFinite(inputQuantity) ? inputQuantity : currentQuantity);
  }

  return count;
}

/** @param {HTMLFormElement | null} form */
function shouldBlockCheckout(form) {
  if (!isSampleLimitEnabled()) return false;

  syncSampleLimitFromDocument();
  return getProjectedSampleCount(form) > getSampleLimitMax();
}

function handleCheckoutGuard(event) {
  const target = event.target;
  if (!(target instanceof HTMLFormElement) || target.id !== 'cart-form') return;

  if (!shouldBlockCheckout(target)) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  openSampleLimitModal();
}

document.addEventListener('submit', handleCheckoutGuard, true);
document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const checkoutButton = target.closest(CHECKOUT_TRIGGER_SELECTOR);
    if (!(checkoutButton instanceof HTMLElement)) return;

    const form =
      checkoutButton instanceof HTMLButtonElement || checkoutButton instanceof HTMLInputElement
        ? checkoutButton.form
        : document.querySelector('form#cart-form');
    if (!shouldBlockCheckout(form)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    openSampleLimitModal();
  },
  true
);
